import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { type ColumnDef } from "@tanstack/react-table";
import { Search, Trash2, Upload, Users } from "lucide-react";
import { confirmAsync } from "@/components/Toaster";
import {
  type ScrapedUser,
  type UsersFilter,
  useDeleteScrapedUser,
  useScrapedUsers,
} from "@/lib/scraper";
import { useChannels } from "@/lib/channels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SimpleSelect } from "@/components/ui/select";
import { ActionMenu } from "@/components/ui/dropdown-menu";
import { DataTable } from "@/components/ui/table";
import { EmptyState } from "@/components/ui/empty-state";
import { PageHeader } from "@/components/ui/page-header";
import { ExportMenu } from "@/components/ui/export-menu";
import { HelpSection } from "@/components/HelpSection";
import { RunControls } from "@/components/audience/RunControls";
import { SourcePicker } from "@/components/audience/SourcePicker";
import { ImportAudienceDialog } from "@/components/audience/ImportAudienceDialog";
import { ImportProgressBanner } from "@/components/audience/ImportProgressBanner";
import { UserDetailDrawer } from "@/components/audience/UserDetailDrawer";
import { LastOnline } from "@/components/audience/last-online";
import { userDisplayName } from "@/lib/audience";
import { UserFlags } from "@/components/audience/UserFlags";
import { cn } from "@/lib/cn";

const PAGE_SIZE = 50;

type ExportFmt =
  | "csv"
  | "xlsx"
  | "json"
  | "txt"
  | "txt-phones"
  | "txt-usernames";

/** Read the `?channel=:id` deep-link (from the Channels page) on first load. */
function readChannelParam(): string | undefined {
  return new URLSearchParams(window.location.search).get("channel") ?? undefined;
}

export function UsersPage() {
  const { t } = useTranslation();

  // Channel membership filter: which channels a user must appear in, and
  // whether they must be in ANY (union) or ALL (intersection) of them.
  // Seeded from the `?channel=:id` deep-link (Channels page → audience).
  const [selectedChannels, setSelectedChannels] = useState<string[]>(() => {
    const c = readChannelParam();
    return c ? [c] : [];
  });
  const [selectedImports, setSelectedImports] = useState<string[]>([]);
  const [channelMode, setChannelMode] = useState<"any" | "all">("any");
  useEffect(() => {
    const sync = () => {
      const c = readChannelParam();
      if (c) {
        setSelectedChannels([c]);
        setChannelMode("any");
      }
    };
    window.addEventListener("popstate", sync);
    return () => window.removeEventListener("popstate", sync);
  }, []);

  const [showImport, setShowImport] = useState(false);

  const [filter, setFilter] = useState<UsersFilter>({
    limit: PAGE_SIZE,
    offset: 0,
    sort: "last_seen_asc", // долго не были = топ для тихого add в канал
  });

  // Live (debounced) search — no lone "OK" button anymore.
  const [search, setSearch] = useState("");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setFilter((f) => ({ ...f, search: search.trim() || undefined, offset: 0 }));
    }, 300);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search]);

  // "In ALL of these" is a channels-only idea: an upload isn't something a
  // person can also be in, so any upload in the selection makes it a union.
  const intersectChannels =
    channelMode === "all" && selectedChannels.length >= 2 && selectedImports.length === 0;

  // Fold the source selection into the API filter.
  const effectiveFilter = useMemo<UsersFilter>(() => {
    const f: UsersFilter = { ...filter };
    if (intersectChannels) f.in_all_channels = selectedChannels;
    else if (selectedChannels.length > 0) f.in_any_channels = selectedChannels;
    if (selectedImports.length > 0) f.from_imports = selectedImports;
    return f;
  }, [filter, selectedChannels, selectedImports, intersectChannels]);

  const users = useScrapedUsers(effectiveFilter);
  const channels = useChannels();
  const del = useDeleteScrapedUser();

  const [detailUser, setDetailUser] = useState<ScrapedUser | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  const total = users.data?.total ?? 0;
  const offset = filter.offset ?? 0;
  const limit = filter.limit ?? PAGE_SIZE;

  // id → channel, for resolving labels on chips and in the filter.
  const channelById = useMemo(
    () => new Map((channels.data ?? []).map((c) => [c.id, c] as const)),
    [channels.data],
  );
  const setSources = (next: { channelIds: string[]; importSources: string[] }) => {
    setSelectedChannels(next.channelIds);
    setSelectedImports(next.importSources);
    setFilter((f) => ({ ...f, offset: 0 }));
  };
  /** Clicking a source badge in the table narrows the page to that source. */
  const onlySource = (next: { channelIds: string[]; importSources: string[] }) => {
    setSources(next);
    setChannelMode("any");
  };

  const onExport = (fmt: ExportFmt) => {
    const params = new URLSearchParams();
    // Map the compound TXT variants back to (fmt, txt_field).
    if (fmt === "txt-phones") {
      params.set("fmt", "txt");
      params.set("txt_field", "phone");
    } else if (fmt === "txt-usernames") {
      params.set("fmt", "txt");
      params.set("txt_field", "username");
    } else {
      params.set("fmt", fmt);
    }
    if (selectedChannels.length > 0) {
      const key = intersectChannels ? "in_all_channels" : "in_any_channels";
      for (const id of selectedChannels) params.append(key, id);
    }
    for (const key of selectedImports) params.append("from_imports", key);
    if (effectiveFilter.min_activity != null)
      params.set("min_activity", String(effectiveFilter.min_activity));
    if (effectiveFilter.has_username != null)
      params.set("has_username", String(effectiveFilter.has_username));
    if (effectiveFilter.is_bot != null)
      params.set("is_bot", String(effectiveFilter.is_bot));
    if (effectiveFilter.is_premium != null)
      params.set("is_premium", String(effectiveFilter.is_premium));
    if (effectiveFilter.online_recently != null)
      params.set("online_recently", String(effectiveFilter.online_recently));
    if (effectiveFilter.language) params.set("language", effectiveFilter.language);
    if (effectiveFilter.search) params.set("search", effectiveFilter.search);
    // Content-Disposition triggers the browser download.
    window.location.href = `/api/exporter/scraped-users?${params.toString()}`;
  };

  const openDetail = (u: ScrapedUser) => {
    setDetailUser(u);
    setDrawerOpen(true);
  };

  const columns = useMemo<ColumnDef<ScrapedUser, unknown>[]>(
    () => [
      {
        id: "user",
        header: t("users.col.user"),
        enableSorting: false,
        cell: ({ row }) => {
          const u = row.original;
          const display = userDisplayName(u);
          const sub =
            u.username && (u.first_name || u.last_name)
              ? [u.first_name, u.last_name].filter(Boolean).join(" ")
              : null;
          return (
            <div className="min-w-0">
              <span className="block truncate font-semibold text-fg-primary">
                {display}
              </span>
              {sub && (
                <span className="block truncate text-xs text-fg-muted">{sub}</span>
              )}
            </div>
          );
        },
      },
      {
        id: "channels",
        header: t("users.col.source"),
        enableSorting: false,
        cell: ({ row }) => {
          const u = row.original;
          const ids =
            u.channel_ids && u.channel_ids.length > 0
              ? u.channel_ids
              : u.first_seen_channel_id
                ? [u.first_seen_channel_id]
                : [];
          if (ids.length === 0) {
            // No channel at all: either the person was imported (then the note
            // taken at import time is the whole story), or they predate the
            // membership table and their origin is simply not recorded.
            if (u.import_label) {
              const label = u.import_label;
              const key = u.import_source;
              return (
                <button
                  type="button"
                  title={
                    u.import_row_source
                      ? t("users.source_imported_row", {
                          label,
                          source: u.import_row_source,
                        })
                      : t("users.source_imported", { label })
                  }
                  disabled={!key}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (key) onlySource({ channelIds: [], importSources: [key] });
                  }}
                >
                  <Badge
                    tone="neutral"
                    variant="subtle"
                    className={cn(
                      "inline-flex max-w-[170px] items-center gap-1 px-2 py-0.5",
                      key && "cursor-pointer hover:bg-bg-elevated",
                    )}
                  >
                    <Upload size={11} className="shrink-0" aria-hidden="true" />
                    <span className="truncate">{label}</span>
                  </Badge>
                </button>
              );
            }
            return (
              <span className="text-fg-muted" title={t("users.source_unknown_hint")}>
                {t("users.source_unknown")}
              </span>
            );
          }
          const shown = ids.slice(0, 2);
          const extra = ids.length - shown.length;
          return (
            <div
              className="flex flex-wrap items-center gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {shown.map((id) => {
                const c = channelById.get(id);
                const label = c
                  ? c.username
                    ? `@${c.username}`
                    : (c.title ?? String(c.tg_channel_id ?? c.id))
                  : id.slice(0, 8);
                return (
                  <button
                    key={id}
                    type="button"
                    title={label}
                    onClick={() => onlySource({ channelIds: [id], importSources: [] })}
                  >
                    <Badge
                      tone="accent"
                      variant="subtle"
                      className="max-w-[150px] cursor-pointer truncate px-2 py-0.5 hover:bg-accent/20"
                    >
                      {label}
                    </Badge>
                  </button>
                );
              })}
              {extra > 0 && (
                <span className="text-xs text-fg-muted">+{extra}</span>
              )}
            </div>
          );
        },
      },
      {
        id: "last_seen",
        header: t("users.col.last_seen"),
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-xs">
            <LastOnline user={row.original} />
          </span>
        ),
      },
      {
        id: "activity",
        header: t("users.col.activity"),
        enableSorting: false,
        cell: ({ row }) =>
          row.original.activity_score != null ? (
            <span className="font-mono text-sm">{row.original.activity_score}</span>
          ) : (
            <span className="text-fg-muted">—</span>
          ),
      },
      {
        id: "lang",
        header: t("users.col.lang"),
        enableSorting: false,
        cell: ({ row }) => (
          <span className="text-fg-secondary">
            {row.original.language_code ?? "—"}
          </span>
        ),
      },
      {
        id: "flags",
        header: t("users.col.flags"),
        enableSorting: false,
        cell: ({ row }) => <UserFlags user={row.original} />,
      },
      {
        id: "actions",
        header: () => <span className="sr-only">{t("users.col.actions")}</span>,
        enableSorting: false,
        cell: ({ row }) => {
          const u = row.original;
          const display = userDisplayName(u);
          return (
            <div className="flex justify-end" onClick={(e) => e.stopPropagation()}>
              <ActionMenu
                label={t("users.col.actions")}
                items={[
                  {
                    label: t("common.delete"),
                    icon: <Trash2 size={15} strokeWidth={2} aria-hidden="true" />,
                    destructive: true,
                    disabled: del.isPending,
                    onSelect: async () => {
                      const yes = await confirmAsync({
                        title: t("users.delete_confirm", { name: display }),
                        confirmKind: "danger",
                        confirmLabel: t("common.delete"),
                      });
                      if (yes) void del.mutate(u.id);
                    },
                  },
                ]}
              />
            </div>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, del.isPending, channelById],
  );

  const hasFilters =
    !!filter.search ||
    !!filter.language ||
    filter.has_username != null ||
    filter.is_bot != null ||
    filter.is_premium != null ||
    filter.online_recently != null ||
    selectedChannels.length > 0 ||
    selectedImports.length > 0;

  const items = users.data?.items ?? [];

  return (
    <section className="mx-auto max-w-7xl space-y-6 px-6 py-8">
      <PageHeader
        title={t("users.title")}
        subtitle={t("users.subtitle")}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setShowImport(true)}>
              <Upload className="mr-2 h-4 w-4" />
              {t("audience_import.button")}
            </Button>
            <ExportMenu<ExportFmt>
              label={t("users_redesign.export")}
              onExport={onExport}
              formats={[
                { value: "csv", label: t("users_redesign.fmt_csv") },
                { value: "xlsx", label: t("users_redesign.fmt_xlsx") },
                { value: "json", label: t("users_redesign.fmt_json") },
                { value: "txt", label: t("users_redesign.fmt_txt") },
                { value: "txt-phones", label: t("users_redesign.fmt_txt_phones") },
                {
                  value: "txt-usernames",
                  label: t("users_redesign.fmt_txt_usernames"),
                },
              ]}
            />
          </div>
        }
      />

      <ImportAudienceDialog open={showImport} onOpenChange={setShowImport} />
      <ImportProgressBanner />

      <RunControls />

      <FiltersBar
        filter={filter}
        search={search}
        onSearch={setSearch}
        onChange={(patch) => setFilter((f) => ({ ...f, ...patch, offset: 0 }))}
      />

      {/* Where this audience came from: channels the scraper walked and lists
          the operator uploaded, each with its head-count. */}
      <SourcePicker
        selection={{ channelIds: selectedChannels, importSources: selectedImports }}
        onChange={setSources}
        footer={
          selectedChannels.length >= 2 && selectedImports.length === 0 ? (
            <div className="flex items-center gap-2">
              <div className="flex gap-1">
                {(["any", "all"] as const).map((m) => (
                  <button
                    key={m}
                    type="button"
                    onClick={() => {
                      setChannelMode(m);
                      setFilter((f) => ({ ...f, offset: 0 }));
                    }}
                    className={cn(
                      "rounded-md px-2 py-1 text-xs font-medium transition-colors",
                      channelMode === m
                        ? "bg-accent text-fg-on-accent"
                        : "bg-bg-secondary text-fg-secondary hover:bg-bg-elevated",
                    )}
                  >
                    {m === "any"
                      ? t("users_redesign.ch_any")
                      : t("users_redesign.ch_all")}
                  </button>
                ))}
              </div>
              <span className="text-xs text-fg-muted">
                {channelMode === "all"
                  ? t("users_redesign.ch_all_hint")
                  : t("users_redesign.ch_any_hint")}
              </span>
            </div>
          ) : null
        }
      />

      <div className="text-sm text-fg-muted">{t("users.total", { count: total })}</div>

      {users.isLoading && <p className="text-fg-muted">{t("users.loading")}</p>}

      {users.data && (
        <DataTable
          columns={columns}
          data={items}
          getRowId={(u) => u.id}
          onRowClick={openDetail}
          empty={
            hasFilters ? (
              <EmptyState
                icon={Users}
                title={t("users_redesign.empty_filtered_title")}
                description={t("users_redesign.empty_filtered_hint")}
              />
            ) : (
              <EmptyState
                icon={Users}
                title={t("users_redesign.empty_title")}
                description={t("users_redesign.empty_hint")}
              />
            )
          }
        />
      )}

      <Pagination
        total={total}
        offset={offset}
        limit={limit}
        onOffset={(o) => setFilter((f) => ({ ...f, offset: o }))}
      />

      <UserDetailDrawer
        user={detailUser}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />

      <HelpSection
        i18nKey="users.help"
        paragraphs={4}
        list={{
          titleKey: "users.help.list_title",
          itemsKey: "users.help.list_items",
          itemCount: 3,
        }}
      />
    </section>
  );
}

// ---------------------------------------------------------------------------
// FiltersBar — search (live) + language + flag chips + standalone Sort
// ---------------------------------------------------------------------------

function FiltersBar({
  filter,
  search,
  onSearch,
  onChange,
}: {
  filter: UsersFilter;
  search: string;
  onSearch: (s: string) => void;
  onChange: (patch: Partial<UsersFilter>) => void;
}) {
  const { t } = useTranslation();

  const flagChips: { key: string; label: string; active: boolean; toggle: () => void }[] =
    [
      {
        key: "has_username",
        label: t("users.filters.has_username"),
        active: filter.has_username === true,
        toggle: () =>
          onChange({ has_username: filter.has_username === true ? undefined : true }),
      },
      {
        key: "no_bot",
        label: t("users.filters.no_bot"),
        active: filter.is_bot === false,
        toggle: () => onChange({ is_bot: filter.is_bot === false ? undefined : false }),
      },
      {
        key: "premium",
        label: t("users.filters.premium"),
        active: filter.is_premium === true,
        toggle: () =>
          onChange({ is_premium: filter.is_premium === true ? undefined : true }),
      },
      {
        key: "online_recently",
        label: t("users.filters.online_recently"),
        active: filter.online_recently === true,
        toggle: () =>
          onChange({
            online_recently: filter.online_recently === true ? undefined : true,
          }),
      },
    ];

  return (
    <Card>
      <CardContent className="space-y-4 pt-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="space-y-1.5">
            <Label htmlFor="f_search">{t("users.filters.search")}</Label>
            <div className="relative">
              <Search
                size={15}
                strokeWidth={2}
                aria-hidden="true"
                className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-fg-muted"
              />
              <Input
                id="f_search"
                value={search}
                onChange={(e) => onSearch(e.target.value)}
                placeholder={t("users_redesign.search_placeholder")}
                className="pl-9"
              />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="f_lang">{t("users.filters.language")}</Label>
            <Input
              id="f_lang"
              value={filter.language ?? ""}
              onChange={(e) => onChange({ language: e.target.value || undefined })}
              placeholder="ru / en / …"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="f_sort">{t("users_redesign.sort_label")}</Label>
            <SimpleSelect
              value={filter.sort ?? "activity"}
              onValueChange={(v) =>
                onChange({ sort: v as UsersFilter["sort"] })
              }
              options={[
                { value: "activity", label: t("users.filters.sort_activity") },
                {
                  value: "last_seen_desc",
                  label: t("users.filters.sort_last_seen_desc"),
                },
                {
                  value: "last_seen_asc",
                  label: t("users.filters.sort_last_seen_asc"),
                },
              ]}
            />
          </div>

          <div className="space-y-1.5">
            <Label>{t("users.filters.flags")}</Label>
            <div className="flex flex-wrap gap-1.5">
              {flagChips.map((c) => (
                <FlagChip key={c.key} label={c.label} active={c.active} onClick={c.toggle} />
              ))}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function FlagChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active}>
      <Badge
        tone={active ? "accent" : "neutral"}
        variant={active ? "subtle" : "outline"}
        className="cursor-pointer px-2.5 py-1 transition-colors"
      >
        {label}
      </Badge>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Pagination — page number + prev/next + jump-to-page
// ---------------------------------------------------------------------------

function Pagination({
  total,
  offset,
  limit,
  onOffset,
}: {
  total: number;
  offset: number;
  limit: number;
  onOffset: (o: number) => void;
}) {
  const { t } = useTranslation();
  const [jump, setJump] = useState("");
  if (total <= limit) return null;

  const page = Math.floor(offset / limit) + 1;
  const pages = Math.max(1, Math.ceil(total / limit));

  const goTo = (p: number) => {
    const clamped = Math.min(Math.max(1, p), pages);
    onOffset((clamped - 1) * limit);
  };

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 text-sm">
      <span className="text-fg-muted">
        {t("users.pagination.page", { page, pages })}
      </span>
      <div className="flex items-center gap-2">
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const n = Number(jump);
            if (Number.isFinite(n) && n > 0) goTo(n);
            setJump("");
          }}
          className="flex items-center gap-1"
        >
          <span className="text-fg-muted">{t("users_redesign.page_jump")}</span>
          <Input
            inputMode="numeric"
            value={jump}
            onChange={(e) => setJump(e.target.value.replace(/\D/g, ""))}
            placeholder={String(page)}
            className="h-8 w-16 text-center"
          />
          <span className="text-fg-muted">
            {t("users_redesign.page_of", { pages })}
          </span>
        </form>
        <Button
          size="sm"
          variant="outline"
          disabled={offset === 0}
          onClick={() => onOffset(Math.max(0, offset - limit))}
        >
          {t("users.pagination.prev")}
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={offset + limit >= total}
          onClick={() => onOffset(offset + limit)}
        >
          {t("users.pagination.next")}
        </Button>
      </div>
    </div>
  );
}
